import { useMemo } from "react";
import { Dropdown, Space } from "antd";
import { EditOutlined, StopOutlined, MoreOutlined, FilePdfOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import TableAntd from "components/elements/Table/Table";
import StatusTag from "components/elements/TagCustom/StatusTag";
import { useTranslation } from "react-i18next";

// NOTE: ใช้ pattern เดียวกับ WarningLetterTable.jsx
// ถ้า dayjs buddhistEra plugin ยังไม่ได้ extend ที่ dayjs config กลาง (global)
// ให้เพิ่ม dayjs.extend(buddhistEra) ที่ dayjs setup file ก่อน ไม่งั้น "BBBB" จะไม่ขึ้น พ.ศ.
function formatDate({ dateString, pattern = "YYYY-MM-DD", reFormat = "DD-MM-YYYY" }) {
    if (!dateString) return "-";
    const date = dayjs(dateString, pattern);
    return date.format(reFormat);
}

const WarningLetterRequestTable = ({
    isLoading = false,
    pagination = {},
    dataSource = [],
    onChange = () => { },
    onView = () => { },
    onViewPdf = () => { },
    onCancelRequest = () => { },
}) => {
    const { t, i18n } = useTranslation();
    const langSuffix = i18n.language === "en" ? "En" : "";

    const columns = useMemo(
        () => [
            {
                title: <div className="text-table">{t("warning_letter.request_id") || "รหัสหนังสือเตือน"}</div>,
                dataIndex: "sequence",
                key: "sequence",
                sorter: true,
            },
            {
                title: <div className="text-table">{t("name-surname") || "ชื่อ-นามสกุลพนักงาน"}</div>,
                dataIndex: "warnedEmployee",
                key: "warnedEmployee",
                sorter: true,
                render: (_, record) =>
                    i18n.language === "en"
                        ? record?.warnedEmployee?.nameAndSurnameEn
                        : record?.warnedEmployee?.nameAndSurname,
            },
            {
                title: <div className="text-table">{t("department") || "แผนก"}</div>,
                dataIndex: "departmentId",
                key: "departmentId",
                sorter: true,
                render: (_, record) =>
                    i18n.language === "en"
                        ? record?.departmentId?.departmentNameEn
                        : record?.departmentId?.departmentName,
            },
            {
                title: <div className="text-table">{t("warning_letter.label.subject_reason") || "เรื่อง/ เหตุผล"}</div>,
                dataIndex: "companyRegulations",
                key: "companyRegulations",
                width: 200,
                render: (_, record) =>
                    i18n.language === "en"
                        ? record?.companyRegulations?.companyRegulationEn
                        : record?.companyRegulations?.companyRegulation,
            },
            {
                title: <div className="text-table">{t("warning_letter.label.date_of_approveDate") || "วันที่ออกหนังสือ"}</div>,
                dataIndex: "approveDate",
                key: "approveDate",
                sorter: true,
                render: (_, record) =>
                    record?.approveDate
                        ? formatDate({
                            dateString: record.approveDate,
                            reFormat: i18n.language === "en" ? "DD MMM YYYY" : "DD MMMM BBBB",
                        })
                        : "-",
            },
            {
                title: <div className="text-table">{t("name-surname-warning") || "ผู้ออกหนังสือ"}</div>,
                dataIndex: "employee",
                key: "employee",
                sorter: true,
                render: (_, record) =>
                    i18n.language === "en"
                        ? record?.employee?.nameAndSurnameEn
                        : record?.employee?.nameAndSurname,
            },
            {
                title: <div className="text-table">{t("status.status") || "สถานะ"}</div>,
                dataIndex: "status",
                key: "status",
                sorter: true,
                render: (_, record) => <StatusTag value={record?.["approveStatus" + langSuffix]} />,
            },
            {
                title: "",
                key: "action",
                render: (_, record) => (
                    <Space size="middle">
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: "1",
                                        onClick: () => onViewPdf(record),
                                        label: t("action.download_template") || "ดูตัวอย่าง PDF",
                                        icon: <FilePdfOutlined style={{ fontSize: "18px", color: "gray" }} />
                                    },
                                    {
                                        key: "2",
                                        onClick: () => onView(record),
                                        label: `${t("action.view")}`,
                                        icon: (
                                            <EyeOutlined style={{ fontSize: "18px", color: "gray" }} />
                                        ),
                                    },
                                ],
                            }}
                        >
                            <MoreOutlined style={{ fontSize: "25px", color: "gray" }} />
                        </Dropdown>
                    </Space>
                ),
            },
        ],
        [i18n.language, langSuffix, t, onView, onViewPdf, onCancelRequest]
    );

    return (
        <TableAntd
            loading={isLoading}
            style={{ backgroundColor: "white", width: "100%" }}
            pagination={{
                ...pagination,
                showTotal: (total, range) =>
                    t("paginate.description")
                        .replace("{min}", range[0])
                        .replace("{max}", range[1])
                        .replace("{total}", total),
            }}
            dataSource={dataSource}
            rowKey={(record) => record.warningLetterRequestId}
            columns={columns}
            onChange={onChange}
        />
    );
};

export default WarningLetterRequestTable;